import { watch } from 'vue';
import { useLayoutStore } from '@/store/layout';
import { useAlertStore } from '@/store/alertStore';

interface ListingFetchParams {
  page: number;
  itemsPerPage: number;
  sort?: string;
  filters?: string;
}

type FetchFn = (params: ListingFetchParams) => Promise<{ data: any[]; total: number }>;
type MutateFn = (data: any) => Promise<any>;

export function useModuleLayoutFetcher(
  moduleName: string,
  fetchFn: FetchFn,
  options?: {
    create?: MutateFn;
    update?: MutateFn;
    remove?: MutateFn;
  }
) {
  const layoutStore = useLayoutStore();
  layoutStore.initLayout(moduleName);

  const getTrackedParams = () => {
    const layout = layoutStore.getLayout(moduleName);
    if (!layout) return null;

    const {
      page,
      itemsPerPage,
      sortBy,
      direction,
      filters
    } = layout;

    const sort = sortBy
      ? (direction === 'desc' ? `-${sortBy}` : sortBy)
      : null;

    return JSON.stringify({ page, itemsPerPage, sort, filters });
  };

  let lastParams = getTrackedParams();

  const reload = async () => {
    const layout = layoutStore.getLayout(moduleName);
    if (!layout) return;

    const { page, itemsPerPage, sortBy, direction, filters } = layout;

    const hasFilters = filters && Object.keys(filters).length > 0;

    const flatFilters = hasFilters
    ? Object.entries(filters)
        .filter(([_, v]) => v != null && v !== '')
        .reduce((acc, [k, v]) => {
          acc[`filter[${k}]`] = v;
          return acc;
        }, {} as Record<string, any>)
    : {};

    const params: ListingFetchParams = {
      page,
      itemsPerPage,
      ...(sortBy && { sort: direction === 'desc' ? `-${sortBy}` : sortBy }),
      ...(hasFilters && flatFilters), // 👈 ya no lo metas dentro de "filters"
    };

    layoutStore.updateLayout(moduleName, { dataLoading: true });

    try {
      const result = await fetchFn(params);
      layoutStore.updateLayout(moduleName, {
        totalItems: result.total,
      });
    } catch (error) {
      console.error(`[${moduleName}] Error fetching data:`, error);
    } finally {
      layoutStore.updateLayout(moduleName, { dataLoading: false });
    }
  };

  watch(
    () => getTrackedParams(),
    async (newParams, oldParams) => {
      if (newParams === oldParams) return;
      await reload();
      lastParams = newParams;
    },
    { immediate: true }
  );

  // Create
  const create = async (data: any) => {
    const alertStore = useAlertStore();
    try {
      if (!options?.create) return;
      await options.create(data);
      alertStore.showAlert('Registro creado exitosamente.', 'success');
      await reload();
    } catch (err: any) {
      console.error(err);
      alertStore.showAlert(
        err?.response?.data?.message || 'Ocurrió un error al crear.',
        'error'
      );
    }
  };

  // Update
  const update = async (data: any) => {
    const alertStore = useAlertStore();
    try {
      if (!options?.update) return;
      await options.update(data);
      alertStore.showAlert('Registro actualizado exitosamente.', 'success');
      await reload();
    } catch (err: any) {
      console.error('[Update Error]', err);

      const message = err?.response?.data?.message || 'Ocurrió un error al actualizar.';
      const errors = err?.response?.data?.errors;

      let fullMessage = message;

      if (errors && typeof errors === 'object') {
        const allMessages = Object.values(errors).flat();
      
        const uniqueMessages = Array.from(new Set(allMessages)); // 👈 Elimina duplicados
      
        const detailedErrors = uniqueMessages.join('\n'); // o '<br>' si es HTML
      
        fullMessage += `\n${detailedErrors}`;
      }

      alertStore.showAlert(fullMessage, 'error');
    }
  };

  // Delete
  const remove = async (data: any) => {
    const alertStore = useAlertStore();
    try {
      if (!options?.remove) return;
      await options.remove(data);
      alertStore.showAlert('Registro eliminado correctamente.', 'success');
      await reload();
    } catch (err: any) {
      console.error(err);
      alertStore.showAlert(
        err?.response?.data?.message || 'Ocurrió un error al eliminar.',
        'error'
      );
    }
  };


  return {
    create,
    update,
    remove,
    reload,
  };
}
